import { AlertsToggleButtonsEnum } from 'app/pages/loanApplication/tabs/alerts/Alerts.constants';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  ChecklistExecutionSnapshotTargetType,
  ChecklistPendingChangeStatusType,
} from 'constants/checklists';
import { ChecklistOverrideType } from 'constants/loanApplication';

import { selectLoanApplication } from './loanApplication.selectors';

export const selectApplicationChecklists = createSelector(
  selectLoanApplication,
  ({ checklists }) => checklists,
);

export const selectApplicationChecklistById = memoizeOne((id, toggleButtonValue) =>
  createSelector(selectApplicationChecklists, checklists => {
    const rawChecklist = checklists[id];

    if (!rawChecklist) {
      return {};
    }

    const isPendingChangesView = toggleButtonValue === AlertsToggleButtonsEnum.WITH_PENDING_CHANGES;
    const withPendingChanges =
      !!rawChecklist.withPendingChanges &&
      (isPendingChangesView || !!rawChecklist.isSavedLaneOutdated);
    const isPendingChangesMarked = withPendingChanges && isPendingChangesView;

    const { categories, items, checklist, ...restChecklistValues } = withPendingChanges
      ? rawChecklist.withPendingChanges
      : rawChecklist;

    const categoriesMap = _.keyBy(categories, 'checklistCategoryId');

    const categoriesIdsWithPendingChanges = isPendingChangesMarked
      ? Object.entries(categoriesMap).reduce((acc, [categoryId, category]) => {
          const isResultChanged = category.result !== category.resultWithoutPendingOverrides;

          if (isResultChanged) {
            acc.push(categoryId);
          }

          return acc;
        }, [])
      : [];

    const itemsWithPendingChanges = isPendingChangesMarked
      ? (rawChecklist.withPendingChanges?.items?.reduce(
          (acc, { checklistItemId, result, resultWithoutPendingOverrides, overrideHistory }) => {
            const isManuallyChanged =
              overrideHistory?.pendingChangeStatus?.type ===
              ChecklistPendingChangeStatusType.READY_TO_APPLY;
            const isPendingChange = result !== resultWithoutPendingOverrides;

            if (isPendingChange) {
              acc.push({ id: checklistItemId, isManuallyChanged });
            }

            return acc;
          },
          [],
        ) ?? [])
      : [];

    return {
      ...restChecklistValues,
      categories,
      items,
      checklist,
      statusDisplayType: checklist?.displaySettings?.statusDisplayType,
      categoriesMap,
      categoriesIdsWithPendingChanges,
      itemsWithPendingChanges,
      itemsMap: _.keyBy(items, 'checklistItemId'),
      pendingChangesCount: itemsWithPendingChanges.filter(
        ({ isManuallyChanged }) => isManuallyChanged,
      ).length,
    };
  }),
);

// Borrower-target checklist snapshots navigate their field links to THAT borrower's
// drawer; loan-wide snapshots return null and the caller falls back to the primary
// borrower (DEV-5218).
export const selectChecklistBorrowerTargetId = memoizeOne(checklistId =>
  createSelector(selectApplicationChecklists, checklists => {
    const target = checklists?.[checklistId]?.target;

    return target?.targetType === ChecklistExecutionSnapshotTargetType.BORROWER
      ? target.targetId
      : null;
  }),
);

const selectApplicationChecklistsValues = createSelector(
  selectApplicationChecklists,
  Object.values,
);

export const selectApplicationChecklistsFormattedValues = createSelector(
  selectApplicationChecklistsValues,
  checklists =>
    checklists
      // `checklists` state carries a `currentChecklistId` scalar alongside the
      // id-keyed checklist entries, so Object.values can include a non-checklist
      // value (e.g. null) — skip anything without a checklist before formatting.
      // Hidden checklists are completeness decisioning engines (DEV-4352) — they
      // drive in-editor highlighting only and never render as a list (DEV-5692).
      .filter(value => !_.isNil(value?.checklist) && !value.checklist.isHidden)
      .map(({ checklist, ...rest }) => {
        const resultItems = _.mapKeys(rest.items, 'checklistItemId');

        return {
          ...rest,
          checklist: {
            ...checklist,
            categories: _.sortBy(checklist.categories, 'orderSequence').map(category => {
              const { items, ...rest } = category;

              return {
                ...rest,
                items: items?.map(item => {
                  const calculations = resultItems[item.id];
                  const calculationsPendingChange = _.get(
                    calculations,
                    'pendingChangeStatus.change',
                  );
                  const isItemPendingOverride =
                    calculationsPendingChange?.type === ChecklistOverrideType.OVERRIDE;

                  return {
                    ...item,
                    calculations: calculationsPendingChange
                      ? {
                          ...calculations,
                          overrideDate: calculationsPendingChange.createdDate,
                          overridenBy: calculationsPendingChange.byUserId,
                          pendingResult: isItemPendingOverride,
                          wasOverriden: isItemPendingOverride,
                        }
                      : calculations,
                  };
                }),
              };
            }),
          },
        };
      }),
);

export const selectCurrentChecklistId = createSelector(
  selectApplicationChecklists,
  checklists => checklists.currentChecklistId,
);
